/**
 * The farrowing hall as the stealth system sees it: a baked light field and a
 * short list of places that hide her.
 *
 * `src/systems/stealth.ts` deliberately knows nothing about scene graphs, Rapier
 * or meshes — a zone hands it static blockers at bake time and a ray query at
 * simulation time. **This file is that hand-over for the farrowing hall**, and it
 * exists so the hall's geometry is stated exactly once: the emitters come from
 * the same `farrowingFixtures()` the renderer lights the room with, and the
 * occluders come from the same `farrowingSolids()` the physics world is built
 * from. A stealth field authored by hand beside a room authored in code is two
 * rooms that agree until somebody moves a kerb.
 *
 * ── The fiction, and it is the design ────────────────────────────────────────
 *
 * **She is invisible until she is out of place.**
 *
 * A man walking a farrowing passage can see into every pen; he is looking at
 * pigs constantly and none of them register. What registers is a pig where a pig
 * cannot be — on the walkway, in the passage, at the door. So the pens are not
 * cover because he cannot *see* into them; they are cover because a piglet in a
 * pen is not information. That is why `PEN_CONCEALMENT` is high and blunt rather
 * than a careful line-of-sight argument about a 0.55 m kerb, which she is short
 * enough to hide behind and he is tall enough to see over — both true, and
 * neither of them the reason.
 *
 * It also states the whole game's stealth grammar in one sentence, which is what
 * a first stealth beat is for. Beat 4's service corridor has no pens in it, and
 * she will feel the difference immediately.
 */

import {
  bakeLightGrid,
  type Aabb,
  type CoverVolume,
  type LightEmitter,
  type LightGrid,
} from '@/systems/stealth';
import { HALL, farrowingFixtures, farrowingSolids } from '@/world/farrowing';
import { bornBayFixtures } from '@/world/bornBay';

/**
 * The height the field is baked at: her back, not the floor.
 *
 * `bakeLightGrid` defaults to y = 0, which asks "is this patch of concrete lit?"
 * The question the encounter actually needs is "is *she* lit standing here?", and
 * the two differ by exactly the thing that matters — a 0.55 m kerb shadows a
 * floor tile beside it and does not shadow a body 0.2 m up. This matches
 * `STEALTH_TUNING.targetEyeHeight`, which is where the visibility ray is aimed,
 * so the light she is standing in and the point he is looking at are the same
 * point.
 */
const SAMPLE_Y = 0.2;

/**
 * A pen interior hides her almost completely. See the fiction, above.
 *
 * Not 1.0, and the remainder is not a rounding allowance: at 0.85 a worker
 * standing directly at her bay's trough, in the brightest part of the hall, can
 * still accumulate suspicion if she stands in the open under the heat lamp for
 * long enough. That is the correct amount of "he would notice eventually", and
 * it is what stops the pens reading as a magic circle.
 */
const PEN_CONCEALMENT = 0.85;

/**
 * The open drain down the centre of the walkway.
 *
 * Only 0.35, and only 0.05 m deep (D-023 made it shallow on purpose — a deeper
 * one captured her heading and she ran the length of it every time). So this is
 * not a hiding place; it is the one lane of the crossing that is better than the
 * others, which turns "run across and hope" into a choice. The dark line was
 * already the strongest leading line in every shot down the hall, and now it
 * means something.
 */
const DRAIN_CONCEALMENT = 0.35;

/** Anything shorter than this cannot shadow a body at `SAMPLE_Y`. */
const OCCLUDER_MIN_TOP = SAMPLE_Y + 0.05;

/**
 * Solids that are architecture rather than obstruction.
 *
 * The floor slabs and the drain bed are below the sample plane; the ceiling is
 * above every emitter. Including them costs a ray test per cell per emitter and
 * can only ever return "no". The walls ARE included — they are at the bounds and
 * they are genuinely what makes the far end fall off.
 */
const NOT_AN_OCCLUDER = ['floor-', 'drain-bed', 'ceiling', 'vestibule-floor', 'vestibule-ceiling'] as const;

function solidToAabb(half: { x: number; y: number; z: number }, pos: { x: number; y: number; z: number }): Aabb {
  return {
    min: { x: pos.x - half.x, y: pos.y - half.y, z: pos.z - half.z },
    max: { x: pos.x + half.x, y: pos.y + half.y, z: pos.z + half.z },
  };
}

/**
 * What blocks light in this hall, from the list the room is actually built from.
 *
 * Matched by name PREFIX rather than by exact name, deliberately: the kerb was
 * one full-length solid until beat 2 cut a gate mouth in it and it became
 * `kerb-l-0` and `kerb-l-1`. A filter written against exact names would have
 * silently stopped shadowing the entire left-hand row on the day the gate went
 * in, and nothing would have failed — the hall would just have got brighter.
 */
export function farrowingOccluders(): Aabb[] {
  const occluders: Aabb[] = [];
  for (const solid of farrowingSolids()) {
    if (NOT_AN_OCCLUDER.some((prefix) => solid.name.startsWith(prefix))) continue;
    const box = solidToAabb(solid.half, solid.pos);
    if (box.max.y < OCCLUDER_MIN_TOP) continue;
    occluders.push(box);
  }
  return occluders;
}

/**
 * The room's lights, as the stealth system understands light.
 *
 * Intensities are normalised to the batten, not taken from the renderer: a
 * `THREE.PointLight`'s intensity is in candela and the field is a 0..1 exposure
 * that gets multiplied into a visibility product. Carrying the renderer's
 * numbers through would make `visibility` an unbounded quantity whose scale
 * depended on a lighting tweak, and every tuning value in `STEALTH_TUNING` is
 * written against 0..1.
 *
 * So the battens are 1 by definition and the heat lamp is 0.6 — it is a small
 * warm pool inside one pen, and it must not out-light the thing the player is
 * being taught to read.
 */
export function farrowingEmitters(): LightEmitter[] {
  const emitters: LightEmitter[] = [];

  for (const fixture of farrowingFixtures()) {
    if (fixture.kind === 'batten') {
      // Length runs along X — see `Fixture.length`'s own comment. A tube is not a
      // point source and modelling it as one puts a circular spotlight under a
      // 1.5 m fitting, which is the shape of a torch rather than of a hall.
      const half = (fixture.length ?? 1.5) / 2;
      emitters.push({
        kind: 'batten',
        start: { x: fixture.position.x - half, y: fixture.position.y, z: fixture.position.z },
        end: { x: fixture.position.x + half, y: fixture.position.y, z: fixture.position.z },
        radius: fixture.range,
        intensity: 1,
      });
    } else if (fixture.kind === 'point') {
      emitters.push({
        kind: 'point',
        position: { ...fixture.position },
        radius: fixture.range,
        intensity: 1,
      });
    } else {
      // **A spot is not a point here, and falling through would be silent.**
      // `LightEmitter` has no directional member, so a spot arriving in a
      // stealth room would be modelled as lighting a whole sphere — the dog
      // would see her standing in the dark above a desk lamp. No stealth room
      // has a spot today; this is what stops the first one being a bug nobody
      // looks for. Adding one means giving `stealth.ts` a cone, not deleting
      // this branch.
      throw new Error(`farrowingEmitters: fixture kind "${fixture.kind}" has no stealth emitter`);
    }
  }

  for (const fixture of bornBayFixtures()) {
    emitters.push({
      kind: 'point',
      position: { ...fixture.position },
      radius: fixture.range,
      intensity: 0.6,
    });
  }

  return emitters;
}

/**
 * Where she is hidden, and by how much.
 *
 * Two volumes and no more. A first stealth beat whose cover the player cannot
 * hold in their head is a first stealth beat that teaches nothing, and every
 * volume added here is a rule the player has to infer from a breathing sound.
 */
export function farrowingCover(): CoverVolume[] {
  const penInner = HALL.walkway + 0.12;
  const cover: CoverVolume[] = [];

  for (const side of [-1, 1] as const) {
    cover.push({
      min: {
        x: side < 0 ? -HALL.halfWidth : penInner,
        y: 0,
        z: -HALL.halfLength,
      },
      max: {
        x: side < 0 ? -penInner : HALL.halfWidth,
        // Up to the kerb top and no further. Standing on something inside the
        // pen — the divider tops out at 0.65, and D-040 keeps that legal —
        // lifts her out of the concealment as well as out of the pen, which is
        // the right answer and comes free from writing the volume as a volume.
        y: 0.55,
        z: HALL.halfLength,
      },
      concealment: PEN_CONCEALMENT,
    });
  }

  cover.push({
    min: { x: -HALL.drainHalfWidth - HALL.drainChamfer, y: -0.1, z: -HALL.halfLength },
    max: { x: HALL.drainHalfWidth + HALL.drainChamfer, y: 0.25, z: HALL.halfLength },
    concealment: DRAIN_CONCEALMENT,
  });

  return cover;
}

/**
 * Bake the hall's light field. Called once at zone load; sampling it is O(1).
 *
 * The returned grid clamps its samples to 1. `bakeLightGrid` sums emitters and
 * does not clamp — correctly, because a bake is a measurement — but two battens
 * whose pools overlap on the walkway would otherwise hand `evaluateVisibility` a
 * `lightLevel` above 1, and `visibility` is a product of four 0..1 terms that
 * `STEALTH_TUNING` is written against. Clamping at the seam between the two
 * keeps the measurement honest and the tuning meaningful.
 */
export function bakeFarrowingLightGrid(): LightGrid {
  const grid = bakeLightGrid(
    {
      minX: -HALL.halfWidth,
      minZ: -HALL.halfLength,
      width: HALL.halfWidth * 2,
      depth: HALL.halfLength * 2,
      sampleY: SAMPLE_Y,
    },
    farrowingEmitters(),
    farrowingOccluders(),
  );

  return {
    ...grid,
    sample: (x: number, z: number): number => Math.min(1, grid.sample(x, z)),
  };
}
